import { useEffect, useState } from "react";
import { useCustom } from "@refinedev/core";
import { FileText } from "lucide-react";

import { getAccessToken } from "../../auth";

type T = (key: string, options?: { args?: unknown[] }) => string;

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

interface Bestand {
  id: string;
  soort: string;
  bestandsnaam: string;
  contentType: string;
}

/**
 * De foto's van het vaccinatieboekje bij een doorgegeven enting (DIER_WIJZIGING). De klant geeft een enting door met
 * een foto van het boekje; de medewerker controleert de gelezen entingen hier naast de foto. Een klik opent de foto
 * op ware grootte. Bij goedkeuren gaat het boekje naar het dierdossier.
 */
export function BoekjeBlok({ t, verzoekId, verwacht }: { t: T; verzoekId: string; verwacht: boolean }) {
  const lijst = useCustom<Bestand[]>({ url: `klantverzoeken/${verzoekId}/bestanden`, method: "get" }).data?.data;
  const boekje = Array.isArray(lijst) ? lijst.filter((b) => b.soort === "VACCINATIEBOEKJE") : [];
  const [urls, setUrls] = useState<Record<string, string>>({});
  const sleutel = boekje.map((b) => b.id).join(",");

  useEffect(() => {
    if (boekje.length === 0) return;
    let weg = false;
    const gemaakt: string[] = [];
    const token = getAccessToken();
    for (const b of boekje) {
      fetch(`${API_URL}/klantverzoeken/${verzoekId}/bestanden/${b.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => {
          if (!blob || weg) return;
          const url = URL.createObjectURL(blob);
          gemaakt.push(url);
          setUrls((oud) => ({ ...oud, [b.id]: url }));
        })
        .catch(() => {});
    }
    return () => {
      weg = true;
      gemaakt.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel, verzoekId]);

  if (boekje.length === 0) {
    return verwacht && lijst !== undefined ? <p className="kvd__stil">{t("klantverzoeken.boekje.nogGeen")}</p> : null;
  }
  return (
    <div className="kvd-boekje">
      <h4>{t("klantverzoeken.boekje.titel")}</h4>
      <p className="kvd__stil">{t("klantverzoeken.boekje.uitleg")}</p>
      <div className="kvd-boekje__fotos">
        {boekje.map((b) => {
          const url = urls[b.id];
          const pdf = b.contentType === "application/pdf";
          return (
            <a key={b.id} className="kvd-boekje__foto" href={url} target="_blank" rel="noreferrer" aria-label={b.bestandsnaam}
              onClick={(e) => { if (!url) e.preventDefault(); }}>
              {url && !pdf ? <img src={url} alt={b.bestandsnaam} /> : <span><FileText size={28} aria-hidden="true" />{b.bestandsnaam}</span>}
            </a>
          );
        })}
      </div>
    </div>
  );
}
